import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  QrCode as QrCodeIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import { laptopAPI } from '../services/api';

const LaptopList = () => {
  const navigate = useNavigate();
  const [laptops, setLaptops] = useState([]);
  const [filters, setFilters] = useState({
    serialNumber: '',
    deviceName: '',
    manufacturer: '',
    model: '',
    user: '',
    status: '',
    location: ''
  });
  const [loading, setLoading] = useState(true);
  const [selectedLaptop, setSelectedLaptop] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalLaptops, setTotalLaptops] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadLaptops();
  }, [page, pageSize, filters, sortBy, sortOrder]);

  const loadLaptops = async () => {
    try {
      setLoading(true);

      const params = {
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
        ...(filters.serialNumber && { search: filters.serialNumber }),
        ...(filters.deviceName && { search: filters.deviceName }),
        ...(filters.manufacturer && { manufacturer: filters.manufacturer }),
        ...(filters.model && { model: filters.model }),
        ...(filters.user && { user: filters.user }),
        ...(filters.status && { status: filters.status }),
        ...(filters.location && { location: filters.location })
      };

      const response = await laptopAPI.getAll(params);
      if (response.data.success) {
        setLaptops(response.data.data);
        setTotalLaptops(response.data.total);
        setTotalPages(response.data.pages);
      }
    } catch (error) {
      console.error('Error loading laptops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      serialNumber: '',
      deviceName: '',
      manufacturer: '',
      model: '',
      user: '',
      status: '',
      location: ''
    });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value));
    setPage(1); // Reset to first page when page size changes
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'success',
      'ready-to-use': 'info',
      'in-repair': 'error',
      'not-ready-to-use': 'warning',
      'no-use': 'default',
      retired: 'default'
    };
    return colors[status] || 'default';
  };

  const handleShowQR = (laptop) => {
    setSelectedLaptop(laptop);
    setQrDialogOpen(true);
  };

  const handleCloseQR = () => {
    setQrDialogOpen(false);
    setSelectedLaptop(null);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Laptop Management
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => navigate('/laptops/new')}
          >
            Add Laptop
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filters</Typography>
              <Button size="small" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Serial Number"
                  value={filters.serialNumber}
                  onChange={(e) => handleFilterChange('serialNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Device Name"
                  value={filters.deviceName}
                  onChange={(e) => handleFilterChange('deviceName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manufacturer"
                  value={filters.manufacturer}
                  onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Model"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="User"
                  placeholder="Name or Email"
                  value={filters.user}
                  onChange={(e) => handleFilterChange('user', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="assigned">Assigned</MenuItem>
                    <MenuItem value="ready-to-use">Ready to Use</MenuItem>
                    <MenuItem value="in-repair">In Repair</MenuItem>
                    <MenuItem value="not-ready-to-use">Not Ready to Use</MenuItem>
                    <MenuItem value="no-use">No Use</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sorting Controls */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Sort By:</Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={sortBy}
                label="Field"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="createdAt">Date Added</MenuItem>
                <MenuItem value="serialNumber">Serial Number</MenuItem>
                <MenuItem value="deviceName">Device Name</MenuItem>
                <MenuItem value="manufacturer">Manufacturer</MenuItem>
                <MenuItem value="model">Model</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="primaryUserEmail">User Email</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Pagination Controls - Top */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Records per page</InputLabel>
                <Select
                  value={pageSize}
                  label="Records per page"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={150}>150</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                Showing {laptops.length === 0 ? 0 : ((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalLaptops)} of {totalLaptops}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                startIcon={<NavigateBefore />}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {totalPages || 1}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                endIcon={<NavigateNext />}
              >
                Next
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : laptops.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No laptops found
                    </TableCell>
                  </TableRow>
                ) : (
                  laptops.map((laptop) => (
                    <TableRow key={laptop._id}>
                      <TableCell>
                        <Button
                          sx={{
                            textTransform: 'none',
                            p: 0,
                            minWidth: 0,
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: 'transparent'
                            }
                          }}
                          onClick={() => navigate(`/laptops/${laptop._id}`)}
                        >
                          {laptop.serialNumber}
                        </Button>
                      </TableCell>
                      <TableCell>{laptop.deviceName}</TableCell>
                      <TableCell>{laptop.manufacturer}</TableCell>
                      <TableCell>{laptop.model}</TableCell>
                      <TableCell>
                        {laptop.primaryUserEmail || '-'}
                        {laptop.primaryUserDisplayName && (
                          <Typography variant="caption" display="block">
                            {laptop.primaryUserDisplayName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={laptop.status}
                          color={getStatusColor(laptop.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {laptop.tags && laptop.tags.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {laptop.tags.slice(0, 3).map((tag, index) => (
                              <Chip key={index} label={tag} size="small" variant="outlined" />
                            ))}
                            {laptop.tags.length > 3 && (
                              <Chip label={`+${laptop.tags.length - 3}`} size="small" variant="outlined" color="default" />
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/laptops/${laptop._id}`)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/laptops/${laptop._id}/edit`)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleShowQR(laptop)}
                          title="Show QR Code"
                        >
                          <QrCodeIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls - Bottom */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {laptops.length === 0 ? 0 : ((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalLaptops)} of {totalLaptops}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                startIcon={<NavigateBefore />}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {totalPages || 1}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                endIcon={<NavigateNext />}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={handleCloseQR}>
        <DialogTitle>QR Code - {selectedLaptop?.serialNumber}</DialogTitle>
        <DialogContent>
          {selectedLaptop?.qrCode && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <img
                src={selectedLaptop.qrCode}
                alt="QR Code"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {selectedLaptop.deviceName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedLaptop.manufacturer} {selectedLaptop.model}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQR}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaptopList;
